import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
} from '@mui/material';
// --- ICON IMPORT ---
import PlaceIcon from '@mui/icons-material/Place';
import { PlaceTwoTone } from '@mui/icons-material';

// Define brand colors locally or import them
const colors = {
  primary: '#005F69',
  primaryAccent: '#F28C28',
  secondaryAccent: '#B9935A',
  text: '#7B3F00',
};

const InlineDestinationCard = ({ destination }) => {
  const { language } = useLanguage();
  const [activeImage, setActiveImage] = useState(null);
  // State to control if the full description is shown
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Set the first image as active when the component mounts or destination changes
    if (destination?.images?.length > 0) {
      setActiveImage(destination.images[0]);
    }
    // Reset the expanded state when the destination changes
    setIsExpanded(false);
  }, [destination]);

  if (!destination) return null;

  // --- Helper variables for name and FULL description ---
  const destName = typeof destination.name === 'object' && destination.name !== null
    ? destination.name[language] || destination.name.en
    : destination.name;
    
  const destDescription = typeof destination.description === 'object' && destination.description !== null
    ? destination.description[language] || destination.description.en
    : destination.description;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // --- NEW LOGIC: TRUNCATION ---
  const TRUNCATE_LENGTH = 650;
  const needsTruncation = destDescription && destDescription.length > TRUNCATE_LENGTH;

  // Determine what text to display based on the expanded state
  const displayText = isExpanded 
    ? destDescription 
    : (needsTruncation ? `${destDescription.substring(0, TRUNCATE_LENGTH)}...` : destDescription);

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 3 },
        my: 3,
        borderRadius: '12px',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* --- Left Column: Image Gallery & Title --- */}
        <Grid item size={{ xs: 12, md: 5 }}>
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              color: colors.text,
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <PlaceTwoTone sx={{ color: colors.primaryAccent, fontSize: '28px' }} />
            {destName}
          </Typography>
          <Box sx={{ borderRadius: 1.5, overflow: 'hidden', mb: 1 }}>
            {activeImage && (
              <Box
                component="img"
                src={activeImage}
                alt={destName || 'Destination Image'}
                sx={{
                  width: '100%',
                  height: 200,
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.4s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {destination.images?.slice(0, 5).map((image, index) => (
              <Box
                key={index}
                component="img"
                src={image}
                alt={`Thumbnail ${index + 1}`}
                onClick={() => setActiveImage(image)}
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: 'cover',
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: activeImage === image ? '2.5px solid' : '2.5px solid transparent',
                  borderColor: colors.primary,
                  transition: 'border-color 0.2s ease-in-out',
                }}
              />
            ))}
          </Box>
        </Grid>

        {/* --- Right Column: Description & Action --- */}
        <Grid item size={{ xs: 12, md: 7 }} sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1 }}>
            {/* --- NEW LOGIC: Display truncated or full text --- */}
            <Typography variant="body2" sx={{ color: colors.text, opacity: 0.9, lineHeight: 1.7, pt: 1 }}>
              {displayText}
            </Typography>
          </Box>

          {/* Action Area (pushed to the bottom) */}
          <Box sx={{ mt: 2, textAlign: 'left' }}>
            {/* --- NEW LOGIC: Only show button if text is actually truncated --- */}
            {needsTruncation && (
                <Button 
                    onClick={handleToggle} 
                    size="small" 
                    sx={{ color: colors.primary, fontWeight: 'bold' }}
                >
                    {isExpanded ? 'Read Less' : 'Read More...'}
                </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default InlineDestinationCard;